import type { Color } from "@asap/design/styles/colors";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import type { DatepickerValue } from "~/components/shared/date/Datepicker.vue";

export type TaskDueDateTone = "overdue" | "today" | "tomorrow" | "later";

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const getTodayDate = () => format(new Date(), "yyyy-MM-dd");

const assertDateOnly = (dateOnly: string) => {
  if (!DATE_ONLY_PATTERN.test(dateOnly)) {
    throw new Error(`Invalid task due date: ${dateOnly}`);
  }
};

export const toTaskDueDay = (dueDate: string): string =>
  DATE_ONLY_PATTERN.test(dueDate) ? dueDate : format(parseISO(dueDate), "yyyy-MM-dd");

const toCalendarDate = (dateOnly: string) => {
  assertDateOnly(dateOnly);
  const [yearPart, monthPart, dayPart] = dateOnly.split("-");
  return new Date(Number(yearPart), Number(monthPart) - 1, Number(dayPart));
};

export const addTaskCalendarDays = (dateOnly: string, days: number): string => {
  assertDateOnly(dateOnly);
  const [yearPart, monthPart, dayPart] = dateOnly.split("-");
  return format(new Date(Number(yearPart), Number(monthPart) - 1, Number(dayPart) + days), "yyyy-MM-dd");
};

export const toTaskDueDateOnly = (value: DatepickerValue): string => {
  if (typeof value === "string") {
    assertDateOnly(value);
    return value;
  }

  if (value instanceof Date) {
    return format(value, "yyyy-MM-dd");
  }

  throw new Error("Invalid task due date");
};

const TONE_COLOR: Record<TaskDueDateTone, Color> = {
  overdue: "red",
  today: "orange",
  tomorrow: "orange",
  later: "gray",
};

export const getTaskDueDateTone = (dueDate: string): TaskDueDateTone => {
  const dueDay = toTaskDueDay(dueDate);
  const today = getTodayDate();

  if (dueDay < today) {
    return "overdue";
  }

  if (dueDay === today) {
    return "today";
  }

  if (dueDay === addTaskCalendarDays(today, 1)) {
    return "tomorrow";
  }

  return "later";
};

export const getTaskDueDateColor = (dueDate: string): Color => TONE_COLOR[getTaskDueDateTone(dueDate)];

export const formatTaskDueDateLabel = (dueDate: string, t: (key: string) => string) => {
  const tone = getTaskDueDateTone(dueDate);

  if (tone === "today") {
    return t("tasks.dates.today");
  }

  if (tone === "tomorrow") {
    return t("tasks.dates.tomorrow");
  }

  return format(toCalendarDate(toTaskDueDay(dueDate)), "d MMM", { locale: fr });
};

export const formatTaskCreatedAt = (createdAt: string | Date) => {
  const date = createdAt instanceof Date ? createdAt : parseISO(createdAt);
  return format(date, "d MMM", { locale: fr });
};
